/**
 * Earnings Section (Combined Earnings & Settlements)
 *
 * 수익 및 정산 통합 섹션 - 신규 디자인 스타일
 * - 역할별 뷰: 리더 수익(earned) / 팔로워 지불(paid)
 * - 정산 내역 목록
 * - 기간별 필터링
 * - 요약 통계
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';

import { getMySettlements } from '@app/api/copyTrading';
import { useMyPortfolio } from '@app/hooks/copyTrading/useMyPortfolio';
import CSelect, { SelectDropdownGlobalStyle } from '@app/components/Common/CSelect';

import type { CopySettlement } from '../../../../types/copyTrading';

interface EarningsSectionProps {
  isMobile?: boolean;
}

export const EarningsSection: React.FC<EarningsSectionProps> = ({ isMobile = true }) => {
  const { t } = useTranslation();
  const { hasPortfolio } = useMyPortfolio();

  // 역할 뷰: earned (리더가 받은 돈) / paid (팔로워가 지불한 돈)
  const [roleView, setRoleView] = useState<'earned' | 'paid'>('paid');

  const [settlements, setSettlements] = useState<CopySettlement[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending'>('all');
  const [periodFilter, setPeriodFilter] = useState<'all' | 'week' | 'month'>('all');

  const summary = useMemo(() => {
    const completedSettlements = settlements.filter(s => s.status === 'completed');
    const pendingSettlements = settlements.filter(s => s.status === 'pending');

    return {
      totalProfitShared: completedSettlements.reduce(
        (sum, s) => sum + Number(s.profitShareAmount || 0), 0
      ),
      pendingAmount: pendingSettlements.reduce(
        (sum, s) => sum + Number(s.profitShareAmount || 0), 0
      ),
      completedCount: completedSettlements.length,
      pendingCount: pendingSettlements.length,
      totalProfit: settlements.reduce(
        (sum, s) => sum + Number(s.totalProfit || 0), 0
      ),
    };
  }, [settlements]);

  useEffect(() => {
    fetchSettlements();
  }, [roleView]);

  const fetchSettlements = async () => {
    setIsLoading(true);
    try {
      const response = await getMySettlements();
      if (response.success) {
        setSettlements(response.data?.settlements || []);
      }
    } catch (error) {
      console.error('[EarningsSection] fetchSettlements error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSettlements = useMemo(() => {
    let result = [...settlements];

    // 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter(s => s.status === statusFilter);
    }

    // 기간 필터
    if (periodFilter !== 'all') {
      const now = dayjs();
      const cutoff = periodFilter === 'week'
        ? now.subtract(7, 'day')
        : now.subtract(30, 'day');
      result = result.filter(s =>
        dayjs(s.settlementPeriodEnd).isAfter(cutoff)
      );
    }

    // 최신순 정렬
    result.sort((a, b) =>
      new Date(b.settlementPeriodEnd).getTime() - new Date(a.settlementPeriodEnd).getTime()
    );

    return result;
  }, [settlements, statusFilter, periodFilter]);

  // Loading
  if (isLoading) {
    return (
      <Container $isMobile={isMobile}>
        <LoadingText>{t('loading')}</LoadingText>
      </Container>
    );
  }

  return (
    <Container $isMobile={isMobile}>
      {/* Header */}
      <SectionHeader $isMobile={isMobile}>
        <SectionTitle $isMobile={isMobile}>{t('earningsSettlements')}</SectionTitle>
      </SectionHeader>

      {/* Role Toggle - 리더인 경우에만 토글 표시 */}
      {hasPortfolio && (
        <RoleToggle>
          <RoleButton
            $isActive={roleView === 'paid'}
            $type="paid"
            onClick={() => setRoleView('paid')}
          >
            {t('profitPaid')}
          </RoleButton>
          <RoleButton
            $isActive={roleView === 'earned'}
            $type="earned"
            onClick={() => setRoleView('earned')}
          >
            {t('profitEarned')}
          </RoleButton>
        </RoleToggle>
      )}

      {/* Summary Stats */}
      <StatsCard>
        {roleView === 'paid' ? (
          <>
            <StatItem>
              <StatLabel>{t('totalPaid')}</StatLabel>
              <StatValue $color="#ff4d4f">
                -{BigNumber(summary.totalProfitShared).toFixed(2)}
                <StatUnit>USDT</StatUnit>
              </StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>{t('totalProfit')}</StatLabel>
              <StatValue $color={summary.totalProfit >= 0 ? '#07d993' : '#ff4d4f'}>
                {summary.totalProfit >= 0 ? '+' : ''}
                {BigNumber(summary.totalProfit).toFixed(2)}
                <StatUnit>USDT</StatUnit>
              </StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>{t('completed')}</StatLabel>
              <StatValue>{summary.completedCount} {t('items')}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>{t('pending')}</StatLabel>
              <StatValue $color="#ffc107">{summary.pendingCount} {t('items')}</StatValue>
            </StatItem>
          </>
        ) : (
          <>
            <StatItem>
              <StatLabel>{t('totalEarned')}</StatLabel>
              <StatValue $color="#07d993">
                +{BigNumber(summary.totalProfitShared).toFixed(2)}
                <StatUnit>USDT</StatUnit>
              </StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>{t('pendingEarnings')}</StatLabel>
              <StatValue $color="#ffc107">
                {BigNumber(summary.pendingAmount).toFixed(2)}
                <StatUnit>USDT</StatUnit>
              </StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>{t('completedSettlements')}</StatLabel>
              <StatValue>{summary.completedCount}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>{t('pendingSettlements')}</StatLabel>
              <StatValue $color="#ffc107">{summary.pendingCount}</StatValue>
            </StatItem>
          </>
        )}
      </StatsCard>

      {/* Filters */}
      <FiltersRow>
        <FilterGroup>
          <FilterButton
            $isActive={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          >
            {t('all')}
          </FilterButton>
          <FilterButton
            $isActive={statusFilter === 'completed'}
            onClick={() => setStatusFilter('completed')}
          >
            {t('completed')}
          </FilterButton>
          <FilterButton
            $isActive={statusFilter === 'pending'}
            onClick={() => setStatusFilter('pending')}
          >
            {t('pending')}
          </FilterButton>
        </FilterGroup>
        <SelectWrapper>
          <SelectDropdownGlobalStyle />
          <CSelect
            variant="default"
            value={periodFilter}
            options={[
              { value: 'all', label: t('allTime') },
              { value: 'week', label: t('last7Days') },
              { value: 'month', label: t('last30Days') },
            ]}
            onChange={(value) => setPeriodFilter(value as 'all' | 'week' | 'month')}
          />
        </SelectWrapper>
      </FiltersRow>

      {/* Settlements List */}
      <SettlementsListCard>
        <ListHeader>
          <ListTitle>
            {t('settlementHistory')} ({filteredSettlements.length})
          </ListTitle>
          <RefreshButton onClick={fetchSettlements}>
            <RefreshIcon src="/assets/newHome/copy-trading/redo-icon.svg" alt="Refresh" />
            {t('refresh')}
          </RefreshButton>
        </ListHeader>

        {filteredSettlements.length === 0 ? (
          <EmptyList>
            <EmptyIcon>💰</EmptyIcon>
            <EmptyText>{t('noSettlementsYet')}</EmptyText>
            <EmptySubtext>{t('settlementsWillAppear')}</EmptySubtext>
          </EmptyList>
        ) : (
          <SettlementsList>
            {filteredSettlements.map((settlement) => (
              <SettlementCard key={settlement.id}>
                <SettlementHeader>
                  <SettlementPeriod>
                    <PeriodDates>
                      {dayjs(settlement.settlementPeriodStart).format('MM/DD')}
                      {' ~ '}
                      {dayjs(settlement.settlementPeriodEnd).format('MM/DD')}
                    </PeriodDates>
                    {settlement.settledAt && (
                      <SettledDate>
                        {t('settledOn')} {dayjs(settlement.settledAt).format('YYYY-MM-DD')}
                      </SettledDate>
                    )}
                  </SettlementPeriod>
                  <StatusTag $status={settlement.status === 'completed' ? 'completed' : 'pending'}>
                    {settlement.status === 'completed' ? t('completed') : t('pending')}
                  </StatusTag>
                </SettlementHeader>

                <SettlementDetails>
                  <DetailItem>
                    <DetailLabel>
                      {roleView === 'paid' ? t('myProfit') : t('followerProfit')}
                    </DetailLabel>
                    <DetailValue $color={Number(settlement.totalProfit) >= 0 ? '#07d993' : '#ff4d4f'}>
                      {Number(settlement.totalProfit) >= 0 ? '+' : ''}
                      {BigNumber(settlement.totalProfit).toFixed(2)} USDT
                    </DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>{t('shareRate')}</DetailLabel>
                    <DetailValue>{settlement.profitShareRate}%</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>
                      {roleView === 'paid' ? t('amountPaid') : t('amountReceived')}
                    </DetailLabel>
                    <DetailValue
                      $color={roleView === 'paid' ? '#ff4d4f' : '#07d993'}
                      $large
                    >
                      {roleView === 'paid' ? '-' : '+'}
                      {BigNumber(settlement.profitShareAmount).toFixed(2)} USDT
                    </DetailValue>
                  </DetailItem>
                </SettlementDetails>
              </SettlementCard>
            ))}
          </SettlementsList>
        )}
      </SettlementsListCard>
    </Container>
  );
};

// Styled Components
const Container = styled.section<{ $isMobile?: boolean }>`
  width: 100%;
  max-width: ${props => props.$isMobile ? '100%' : '1728px'};
  margin: 0 auto;
  margin-top: 32px;
  padding: ${props => props.$isMobile ? '0 16px' : '0 144px'};
  display: flex;
  flex-direction: column;
  gap: ${props => props.$isMobile ? '16px' : '32px'};
`;

const LoadingText = styled.div`
  width: 100%;
  padding: 60px 0;
  text-align: center;
  color: #ffffff;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
`;

const SectionHeader = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.$isMobile ? '16px' : '46px'};
`;

const SectionTitle = styled.h2<{ $isMobile?: boolean }>`
  font-family: 'Aggro', sans-serif;
  font-size: ${props => props.$isMobile ? '20px' : '28px'};
  font-weight: 500;
  color: #ffffff;
  letter-spacing: ${props => props.$isMobile ? '-0.4px' : '-0.56px'};
  line-height: ${props => props.$isMobile ? '28px' : '1.2'};
  flex-shrink: 0;
`;

const RoleToggle = styled.div`
  display: flex;
  background: rgba(91, 93, 107, 0.3);
  border-radius: 8px;
  padding: 4px;
`;

const RoleButton = styled.button<{ $isActive: boolean; $type: 'paid' | 'earned' }>`
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: ${props => props.$isActive ? 700 : 400};
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${props => props.$isActive
    ? props.$type === 'paid'
      ? 'rgba(255, 77, 79, 0.15)'
      : 'rgba(7, 217, 147, 0.15)'
    : 'transparent'
  };
  color: ${props => props.$isActive
    ? props.$type === 'paid'
      ? '#ff4d4f'
      : '#07d993'
    : '#b3b5bd'
  };

  &:hover {
    background: ${props => !props.$isActive && 'rgba(255, 255, 255, 0.05)'};
  }
`;

const StatsCard = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 20px 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  color: #b3b5bd;
`;

const StatValue = styled.div<{ $color?: string }>`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: ${props => props.$color || '#ffffff'};
`;

const StatUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: inherit;
  margin-bottom: 2px;
`;

const FiltersRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterButton = styled.button<{ $isActive: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: ${props => props.$isActive ? 700 : 400};
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${props => props.$isActive ? 'rgba(7, 217, 147, 0.15)' : 'rgba(91, 93, 107, 0.3)'};
  color: ${props => props.$isActive ? '#07d993' : '#b3b5bd'};
  border: none;

  &:hover {
    background: ${props => props.$isActive ? 'rgba(7, 217, 147, 0.2)' : 'rgba(91, 93, 107, 0.4)'};
  }
`;

const SelectWrapper = styled.div`
  min-width: 100px;

  .ant-select {
    width: 100%;
    height: 34px;
    background: rgba(91, 93, 107, 0.3);
    border-radius: 8px;
  }

  .ant-select-selector {
    background: transparent !important;
    border: none !important;
    height: 34px !important;
    padding: 0 12px !important;
  }

  .ant-select-selection-item {
    color: #b3b5bd;
    font-family: 'NanumSquareRound', sans-serif;
    font-size: 13px;
    line-height: 34px !important;
  }

  .ant-select-arrow {
    color: #b3b5bd;
  }

  .ant-select-focused .ant-select-selector {
    box-shadow: none !important;
  }
`;

const SettlementsListCard = styled.div`
  background: #0c2323;
  border-radius: 16px;
  overflow: hidden;
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const ListTitle = styled.h3`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #b3b5bd;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const RefreshIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const SettlementsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SettlementCard = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const SettlementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const SettlementPeriod = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PeriodDates = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
`;

const SettledDate = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 11px;
  color: #777986;
`;

const StatusTag = styled.span<{ $status: 'completed' | 'pending' }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 700;
  background: ${props =>
    props.$status === 'completed' ? 'rgba(7, 217, 147, 0.2)' : 'rgba(255, 193, 7, 0.2)'
  };
  color: ${props =>
    props.$status === 'completed' ? '#07d993' : '#ffc107'
  };
`;

const SettlementDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailLabel = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 11px;
  color: #777986;
`;

const DetailValue = styled.div<{ $color?: string; $large?: boolean }>`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: ${props => props.$large ? '16px' : '14px'};
  font-weight: 700;
  color: ${props => props.$color || '#ffffff'};
`;

const EmptyList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
`;

const EmptyText = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-top: 12px;
`;

const EmptySubtext = styled.div`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  color: #777986;
  margin-top: 4px;
`;

export default EarningsSection;
